<template>
  <section class="create-studio">
    <div class="background" />
    <div
      v-if="studio"
      class="wrap"
    >
      <h2 class="create-studio-title page-title">Create Studio Profile</h2>
      <transition
        appear
        mode="out-in"
        name="slide-fade"
      >
        <component
          :is="stages[currentStage]"
          :component="stages[currentStage]"
          :studio.sync="studio"
          :studio-image="studioImage"
          :change-image="changeImage"
          class="step-wrap"
        />
      </transition>
      <div class="navigation">
        <div class="navigation__wrap-dots mt-6">
          <span
            v-for="(_, index) in stages"
            :key="index"
            :class="index === currentStage ? 'navigation__dot--active' : ''"
            class="navigation__dot"
          />
        </div>
        <div class="navigation__wrap-btn mb-10">
          <div class="d-flex flex-column flex-sm-row-reverse nav-buttons">
            <v-btn
              class="navigation__btn navigation__btn--next"
              color="var(--color-blue-800)"
              height="52"
              @click="toNextStage"
            >
              {{ currentStage < stages.length - 1 ? 'next' : 'create studio' }}
            </v-btn>
            <v-btn
              :disabled="currentStage <= 0"
              class="navigation__btn"
              color="var(--color-blue-800)"
              height="52"
              outlined
              @click="toPreviousStage"
            >
              back
            </v-btn>
          </div>
          <v-btn
            block
            class="navigation__btn-cancel"
            color="var(--color-blue-800)"
            height="52"
            max-width="194"
            text
            @click="cancelStudioCreation"
          >
            cancel
          </v-btn>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { Studio } from '@/modules/Studios/types'
import CreateStudioInfo from '@/modules/Studios/components/create-studio/CreateStudioInfo.vue'
import CreateStudioAdmins from '@/modules/Studios/components/create-studio/CreateStudioAdmins.vue'
import CreateStudioTeachers from '@/modules/Studios/components/create-studio/CreateStudioTeachers.vue'
import CreateStudioStudents from '@/modules/Studios/components/create-studio/CreateStudioStudents.vue'
import CreateStudioSocials from '@/modules/Studios/components/create-studio/CreateStudioSocials.vue'
import CreateStudioLocation from '@/modules/Studios/components/create-studio/CreateStudioLocation.vue'
import { StudioApi } from '@/enitites/studio'
import { useImage } from '@/shared/composables/image'

const router = useRouter()
const route = useRoute()
const { saveImage, getStudioImage } = useImage()

const stages = [
  CreateStudioInfo,
  CreateStudioLocation,
  CreateStudioSocials,
  CreateStudioAdmins,
  CreateStudioTeachers,
  CreateStudioStudents,
]
const currentStage = ref(0)
const studio = ref<Studio>()
const studioImage = ref('')
const studioImageFile = ref<FileList | null>(null)

// Image handling
const changeImage = (e: Event) => {
  const target = e.target as HTMLInputElement
  const filesImg = target.files

  if (!filesImg || filesImg.length === 0) return

  const reader = new FileReader()
  reader.onload = (event) => {
    studioImage.value = event.target?.result as string
    studioImageFile.value = filesImg
  }
  reader.readAsDataURL(filesImg[0])
}

// Studio update
const handleUpdateStudio = async () => {
  if (!studio.value) return

  // Handle image upload if on first stage
  if (currentStage.value === 0 && studioImageFile.value) {
    studio.value.image = await saveImage(studioImageFile.value[0])
  }

  await StudioApi.updateStudio(studio.value)
}

const publishStudio = async () => {
  if (!studio.value) return
  const publishedStudio = await StudioApi.publishStudio(studio.value.id)
  if (publishedStudio) {
    await router.push({ name: 'Studio', params: { id: String(studio.value.id) } })
  }
}

const toNextStage = async () => {
  await handleUpdateStudio()

  // Navigate to the next stage
  const nextStage = currentStage.value + 1
  if (stages[nextStage]) {
    currentStage.value = nextStage
  } else {
    // If it's the last stage, publish the studio
    await publishStudio()
  }
}

const toPreviousStage = async () => {
  await handleUpdateStudio()

  const previousStage = currentStage.value - 1
  if (stages[previousStage]) {
    currentStage.value = previousStage
  }
}

const cancelStudioCreation = async () => {
  if (!studio.value) return
  await StudioApi.deleteStudio(studio.value.id)
  await router.push({ name: 'Studios' })
}

onMounted(async () => {
  const studioId = route.params.id
  studio.value = await StudioApi.fetchStudio(studioId)

  // Initialize studio image if studio has an image
  if (studio.value && studio.value.image) {
    studioImage.value = getStudioImage(studio.value.image, 1000)
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';
@import '@/assets/style/navigationEventCreate';
@import '@/assets/style/slideFade';

.create-studio {
  position: relative;
  height: 100%;
  padding-top: var(--header-height);
  background-color: #fff;

  .background {
    position: absolute;
    inset: var(--header-height) 0 0;
    width: 100%;
    height: 100%;
    background: #fff url('/img/page-create-bg.png') no-repeat center top / cover;
    background-size: auto min(100vh, 666px);
  }

  .wrap {
    position: relative;
    max-width: 600px;
    margin: 100px auto 72px;
    padding: 0 23px;

    .create-studio-title {
      @include font-h1;

      margin-bottom: 40px;
      text-align: center;
      text-transform: uppercase;
    }

    .step-wrap {
      width: 100%;
      max-width: 420px;
      margin: 0 auto;
    }
  }

  .navigation {
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
  }

  .nav-buttons {
    gap: 20px;
  }

  .navigation__btn {
    margin: 0;
  }

  .navigation__btn--next {
    color: var(--color-white);
  }

  .navigation__btn-cancel {
    margin-top: 8px;
  }
}
</style>
